/**
 * AwaitingSignaturesStuckTable — carts >14d in awaiting-signatures.
 *
 * Like PipelineErrorsTable, this surface is wired against the records
 * browse output until backend exposes a dedicated stuck-signing query.
 * Resend / abort actions will become live once the backend route lands;
 * today they are disabled with a tooltip.
 */

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, humanAgo, rowsToCsv } from '@/utils/time';

interface StuckRow {
  cartId: string;
  branch?: string;
  customerName?: string;
  ageDays: number;
  awaitingSince?: string;
}

const DAYS_THRESHOLD = 14;

export function AwaitingSignaturesStuckTable(): JSX.Element {
  const api = useApiClient();
  const [rows, setRows] = useState<StuckRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.retail.superAdmin.records.browse({
        entityType: 'Cart',
        ageDays: DAYS_THRESHOLD,
        top: 200,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      const mapped: StuckRow[] = data.map((r) => {
        const out: StuckRow = {
          cartId: r.entityKey,
          ageDays: r.ageDays,
        };
        if (r.branch) out.branch = r.branch;
        if (r.customerName != null) out.customerName = r.customerName;
        if (r.resolvedAt) out.awaitingSince = r.resolvedAt;
        return out;
      });
      setRows(mapped);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const friendly = msg.includes('404') || msg.toLowerCase().includes('not found')
        ? 'Awaiting-signature stuck view — backend route pending. Check back after the next deploy.'
        : msg;
      setError(friendly);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(
      `stuck-signing-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
    );
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Awaiting-signature stuck (&gt;{DAYS_THRESHOLD}d)</h3>
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="empty">
          No stuck signing carts. (Note: action buttons (Resend / Abort)
          ship once /v3/incidents/awaiting-signatures-stuck lands.)
        </div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Cart</th>
              <th>Branch</th>
              <th>Customer</th>
              <th>Age</th>
              <th>Awaiting since</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cartId}>
                <td>
                  <code>{r.cartId.slice(0, 8)}…</code>
                </td>
                <td>{r.branch ?? '—'}</td>
                <td>{r.customerName ?? '—'}</td>
                <td>{r.ageDays}d</td>
                <td>
                  {r.awaitingSince
                    ? `${humanAgo(r.awaitingSince)} (${formatLocal(r.awaitingSince)})`
                    : '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled
                    title="Resend / abort actions ship with the dedicated backend route."
                  >
                    Resend
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
